import { useEffect } from "react";
import { MouseEvent, useState } from "react";
import { useAppDispatch } from "../../redux/hooks";
import { HomepageBannerProps } from "./types";
import styles from "./home.module.css";

// Swiper
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { publishModalData, publishModalVisibility } from "../../redux/features/modal/modalSlice";
import PrizeTicketSelector from "../../components/PrizeTicketSelector/prizeTicketSelector";
import DOMPurify from "dompurify";

const sanitizeBannerHtml = (html: string) => {
  const container = document.createElement("div");
  container.innerHTML = DOMPurify.sanitize(html);

  container.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach(heading => {
    const replacement = document.createElement("div");
    const originalTagName = heading.tagName.toLowerCase();

    Array.from(heading.attributes).forEach(attribute => {
      replacement.setAttribute(attribute.name, attribute.value);
    });

    replacement.classList.add("banner-heading", `banner-${originalTagName}`);
    replacement.innerHTML = heading.innerHTML;
    heading.replaceWith(replacement);
  });

  return container.innerHTML;
};

const HomepageBannerSlides = ({
  mediaList,
  slideAutoPlay,
  slideDelay,
}: {
  mediaList: HomepageBannerProps[];
  slideAutoPlay: boolean;
  slideDelay: number;
}) => {
  const [isBannerLoaded, setIsBannerLoaded] = useState(false);
  const dispatch = useAppDispatch();

  // Auto play config
  const autoPlayConfig = () => {
    if (slideAutoPlay) {
      return {
        delay: slideDelay,
        disableOnInteraction: true,
      };
    } else {
      return false;
    }
  };

  // Add to cart on click
  const onBannerAddToCartClick = (event: MouseEvent<HTMLButtonElement>, prizeId: number) => {
    event.stopPropagation();
    dispatch(
      publishModalData({
        modalData: (
          <PrizeTicketSelector
            {...{
              prizeId,
            }}
          />
        ),
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  useEffect(() => {
    if (isBannerLoaded) {
      var slides = document.getElementsByClassName("home-banner-slide");
      for (var i = 0; i <= slides.length - 1; i++) {
        var bannerCta = slides[i].querySelector(".banner-cta");
        var addToCartButton = slides[i].getElementsByClassName("home-banner-add-to-cart-button");

        if (bannerCta !== null && addToCartButton.length > 0) {
          // Set cta button style to secondary
          var htmlButton = slides[i].querySelector("a.btn");
          htmlButton.classList.add("secondary");

          var buttonWidth = htmlButton.getBoundingClientRect().width;
          addToCartButton[0].setAttribute("style", "margin-left:" + buttonWidth + "px");
        }
      }
    }
  }, [isBannerLoaded]);

  const pagination = {
    clickable: true,
    el: ".hero-banner-pagination",
    renderBullet: function (index: any, className: string) {
      return (
        '<span class="' +
        className +
        ' hero-banner-pagination-bullet !m-0 !p-0 !border-none !size-1.5 !bg-white/38 transition-all opacity-100"></span>'
      );
    },
  };

  return (
    <div className={"aspect-square lg:aspect-[2496/787]"}>
      <Swiper
        className="swiper-container h-full"
        navigation={{
          prevEl: ".homepage-banner-prev",
          nextEl: ".homepage-banner-next",
        }}
        pagination={pagination}
        autoplay={autoPlayConfig()}
        // autoHeight removed: it was overriding the aspect-ratio wrapper and resizing the
        // container as each slide loaded, causing CLS 1.014 on mobile. The wrapper div's
        // aspect-square lg:aspect-[2496/787] class already correctly sizes the Swiper container.
        loop={true}
        modules={[Autoplay, Navigation, Pagination]}
        observeParents={true}
        observer={true}
        onInit={() => {
          setTimeout(() => {
            setIsBannerLoaded(true);
          }, 100);
        }}>
        {mediaList?.map((media, index) => (
          <SwiperSlide key={index} className="item home-banner-slide h-full">
            <>
              {/* Banner CTA */}
              <div
                className={`absolute top-[50%] left-[50%] w-[95%] opacity-[1] z-[1] text-center ${styles.bannerCtaContainer}`}>
                <div
                  className={`relative max-[540px]:text-center ${styles.bannerCtaInnerContainer}`}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeBannerHtml(media.Html),
                  }}></div>

                {/* Add to cart button */}
                {media.CompetitionId != null && media.PrizeId != null && (
                  <button
                    data-prize-id={media.PrizeId}
                    className={`home-banner-add-to-cart-button ml-[12px] justify-center items-center bottom-0 absolute left-[165px] w-[44.7px] h-[44.7px] ${styles.cartButton}`}
                    onClick={e => onBannerAddToCartClick(e, media.PrizeId)}>
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5.00065 14.5671C5.44268 14.5671 5.8666 14.7427 6.17916 15.0552C6.49172 15.3678 6.66732 15.7917 6.66732 16.2337C6.66732 16.6758 6.49172 17.0997 6.17916 17.4122C5.8666 17.7248 5.44268 17.9004 5.00065 17.9004C4.55862 17.9004 4.1347 17.7248 3.82214 17.4122C3.50958 17.0997 3.33398 16.6758 3.33398 16.2337C3.33398 15.7917 3.50958 15.3678 3.82214 15.0552C4.1347 14.7427 4.55862 14.5671 5.00065 14.5671ZM5.00065 14.5671H14.1673M5.00065 14.5671V2.90039H3.33398M14.1673 14.5671C14.6093 14.5671 15.0333 14.7427 15.3458 15.0552C15.6584 15.3678 15.834 15.7917 15.834 16.2337C15.834 16.6758 15.6584 17.0997 15.3458 17.4122C15.0333 17.7248 14.6093 17.9004 14.1673 17.9004C13.7253 17.9004 13.3014 17.7248 12.9888 17.4122C12.6762 17.0997 12.5007 16.6758 12.5007 16.2337C12.5007 15.7917 12.6762 15.3678 12.9888 15.0552C13.3014 14.7427 13.7253 14.5671 14.1673 14.5671ZM5.00065 4.56706L16.6673 5.40039L15.834 11.2337H5.00065"
                        stroke="#F7F7F7"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <picture>
                {media.MobileImage != "" && <source srcSet={media.MobileImage} media="(max-width: 540px)" />}
                {media.DesktopImage != "" && <source srcSet={media.DesktopImage} media="(min-width: 540px)" />}

                {/* First slide: eager + high priority so the browser fetches the image
                    immediately rather than queuing it behind other resources. This eliminates
                    the black flash that appeared after Swiper mounted but before the image arrived. */}
                <img
                  className={`relative w-full h-full object-cover`}
                  src={media.DesktopImage}
                  alt="BotB-banner-image"
                  width={2496}
                  height={787}
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "auto"}
                />

                {/* Test images */}
                {/* {media.MobileImage != "" && (
                  <source
                    srcSet="https://cdn.botb.com/media/t0ebsde0/botb-4259-slider-mobile-1000x1000.webp"
                    media="(max-width: 540px)"
                  />
                )}
                {media.DesktopImage != "" && (
                  <source
                    srcSet="https://cdn.botb.com/media/4r4fs4wh/botb-4325-slide-desktop-2496-787.webp"
                    media="(min-width: 540px)"
                  />
                )}

                <img
                  className={`relative w-[100%] h-auto`}
                  src="https://cdn.botb.com/media/4r4fs4wh/botb-4325-slide-desktop-2496-787.webp"
                  alt="BotB-banner-image"
                /> */}
              </picture>
            </>

            <div
              className={
                "max-lg:hidden absolute inset-x-0 w-full bottom-0 z-[0] h-[189px] [background:linear-gradient(0deg,#000_0%,rgba(0,0,0,0.00)_63.76%)]"
              }
            />
          </SwiperSlide>
        ))}

        {/* Prev */}
        <div
          className={`homepage-banner-prev flex justify-center items-center absolute top-[50%] left-[10px] w-[40px] h-[40px] z-[1] cursor-pointer ${styles.homepageBannerPrev}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Next */}
        <div
          className={`homepage-banner-next flex justify-center items-center absolute top-[50%] right-[10px] w-[40px] h-[40px] z-[1] cursor-pointer ${styles.homepageBannerNext}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div className="hero-banner-pagination absolute flex items-center justify-center !left-1/2 !w-auto -translate-x-1/2 gap-2 !bottom-5 lg:!bottom-20 z-[2]" />
      </Swiper>
    </div>
  );
};

export default HomepageBannerSlides;
